import { Address, LINKS, OracleKey, ProtocolKey } from "@silo/types";
import { cn } from "../../../utils/class-names";
import { match } from "ts-pattern";
import { OracleAssets } from "../types";
import {
  AnalyticsLink,
  FeedDescription,
  LearnMoreAboutChainlinkLink,
  LearnMoreLink,
  PendleOracleLabel,
  RiskDescription,
  SymbioticOracleLabel
} from "./components";
import { MARKET_ADDRESS } from "@silo/config";
import { PlainLink } from "../../link";
import { DescriptionExternalLink } from "../../description";

interface DescriptionProps {
  className?: string;
  oracleKey: OracleKey;
  oracleAssets: OracleAssets;
  marketAddress: Address;
  marketName: string;
  marketSymbol: string;
  rawMarketSymbol: string;
  protocolKey: ProtocolKey;
}

export function Description({
  className,
  oracleKey,
  oracleAssets,
  marketAddress,
  marketSymbol,
  marketName,
  rawMarketSymbol,
  protocolKey,
}: DescriptionProps): JSX.Element {
  return (
    <div className={cn("flex flex-col gap-5", className)}>
      {match(oracleKey)
        .with("uniswap-v3", () => (
          <>
            <FeedDescription
              oracleKey={oracleKey}
              oracleAssets={oracleAssets}
            />

            <RiskDescription oracleKey={oracleKey} marketName={marketName} />

            <AnalyticsLink
              protocolKey={protocolKey}
              rawMarketSymbol={rawMarketSymbol}
            />

            <LearnMoreLink />
          </>
        ))
        .with("uniswap-v3-v2", () => (
          <>
            <FeedDescription
              oracleKey={oracleKey}
              oracleAssets={oracleAssets}
            />

            <RiskDescription oracleKey={oracleKey} marketName={marketName} />

            <AnalyticsLink
              protocolKey={protocolKey}
              rawMarketSymbol={rawMarketSymbol}
            />

            <LearnMoreLink />
          </>
        ))
        .with("uniswap-v3-v3", () => {
          const isUwuSilo =
            protocolKey === "mainnetLlama" &&
            marketAddress === MARKET_ADDRESS.mainnetLlama.UWU;

          return (
            <>
              {isUwuSilo ? (
                <p>
                  The protocol uses the UWU/ETH Uniswap V3 pool to read the
                  value of UWU. Given that the UniV3 UWU DEX liquidity is
                  provided by a few{" "}
                  <PlainLink
                    external
                    href="https://debank.com/protocols/pool/0x98c58833efb950291a2f13978291d9b18c754404/eth/holders"
                  >
                    liquidity providers
                  </PlainLink>{" "}
                  (LPs), LPs can remove liquidity to manipulate UWU price upward
                  to borrow more than the system allows. Silo Finance cannot
                  protect you from risk. Do your research.{" "}
                </p>
              ) : (
                <>
                  <FeedDescription
                    oracleKey={oracleKey}
                    oracleAssets={oracleAssets}
                  />

                  <RiskDescription
                    oracleKey={oracleKey}
                    marketName={marketName}
                  />
                </>
              )}

              <AnalyticsLink
                protocolKey={protocolKey}
                rawMarketSymbol={rawMarketSymbol}
              />

              <LearnMoreLink />
            </>
          );
        })
        .with("balancer", () => (
          <>
            <FeedDescription
              oracleKey={oracleKey}
              oracleAssets={oracleAssets}
            />

            <RiskDescription oracleKey={oracleKey} marketName={marketName} />

            <LearnMoreLink />
          </>
        ))
        .with("chainlink", () => (
          <>
            <FeedDescription
              oracleKey={oracleKey}
              oracleAssets={oracleAssets}
            />

            <RiskDescription oracleKey={oracleKey} marketName={marketName} />

            <LearnMoreLink />
          </>
        ))
        .with("wstETH-oracle", () => (
          <>
            <FeedDescription
              oracleKey={oracleKey}
              oracleAssets={oracleAssets}
            />

            <p>
              wstETH uses a custom oracle that computes wstETH value based on
              Chainlink stETH/ETH data feed and wstETH-stETH Exchange Rate. Silo
              Protocol cannot verify the precision or security of both data
              feeds.{" "}
              <span className="text-amber-300">Use at your own risk.</span>
            </p>

            <LearnMoreLink />
          </>
        ))
        .with("gOHM-oracle", () => (
          <>
            <FeedDescription
              oracleKey={oracleKey}
              oracleAssets={oracleAssets}
            />

            <p>
              gOHM uses a custom oracle that computes gOHM value based on
              Chainlink OHM/ETH data feed and OHM rebase index. Silo Protocol
              cannot verify the precision or security of the Chainlink feed or
              OHM rebase index.{" "}
              <span className="text-amber-300">Use at your own risk.</span>
            </p>

            <LearnMoreLink />
          </>
        ))
        .with("dia", () => (
          <>
            <FeedDescription
              oracleKey={oracleKey}
              oracleAssets={oracleAssets}
            />

            <p>
              While DIA is considered a trusted price provider, Silo Protocol
              cannot verify the security of their data feeds or control their
              behavior.{" "}
              <span className="text-amber-300">Use at your own risk.</span>
            </p>

            <LearnMoreLink />
          </>
        ))
        .with("curve-lp", () => {
          /**
           * In theory this should be a silo config value. But since it's a niche UI
           * treatment that only applies for a specific Curve LP silo, just
           * hardcoding the logic here for now. If this continues to scale, then we
           * can evaluate if it belongs in the silo config.
           */
          function getMinimumPriceSymbol(): string {
            if (
              protocolKey === "mainnetLegacy" &&
              marketAddress === MARKET_ADDRESS.mainnetLegacy.stETH_NG
            ) {
              // TODO (mainnet-llama)
              return "ETH/stETH";
            }

            return marketSymbol;
          }

          return (
            <>
              <p>
                The base asset in this market uses a custom oracle that performs
                a lower-bound estimation for {marketSymbol} LP token value by
                multiplying its Curve virtual price with the minimum price of{" "}
                {getMinimumPriceSymbol()} as reported by a Chainlink data feed.
                While Silo protocol implements audited guards against LP token
                price manipulation, unknown risks might be present.{" "}
                <span className="text-amber-300">Use at your own risk.</span>
              </p>

              <LearnMoreLink />

              <DescriptionExternalLink href={LINKS.SILOPEDIA.AUDITED}>
                Read ChainSecurity audit report
              </DescriptionExternalLink>
            </>
          );
        })
        .with("crvUSD-oracle", () => (
          <>
            <p>
              The protocol uses the{" "}
              <PlainLink external href={LINKS.CRV_USD.PRICE_ORACLE}>
                crvUSD price oracle
              </PlainLink>{" "}
              embedded in the{" "}
              <PlainLink external href={LINKS.CRV_USD.USDC_CURVE_POOL}>
                crvUSD/USDC Curve pool
              </PlainLink>{" "}
              to return the value of crvUSD denominated in USDC. To compute the
              value of crvUSD in ETH, the protocol uses{" "}
              <PlainLink external href={LINKS.CRV_USD.USDC_ETH_CHAINLINK}>
                USDC/ETH Chainlink data feed
              </PlainLink>
              .
            </p>

            <LearnMoreLink />
          </>
        ))
        .with("redstone", () => (
          <>
            <FeedDescription
              oracleKey={oracleKey}
              oracleAssets={oracleAssets}
            />

            <p>
              While RedStone is considered a trusted price provider, Silo
              Protocol cannot verify the security of their data feeds or control
              their behavior.{" "}
              <span className="text-amber-300">Use at your own risk.</span>
            </p>

            <LearnMoreLink />
          </>
        ))
        .with("chronicle", () => (
          <>
            <FeedDescription
              oracleKey={oracleKey}
              oracleAssets={oracleAssets}
            />

            <p>
              While Chronicle feeds are broadly trusted, Silo Protocol cannot verify the security of the feeds.{" "}
              <span className="text-amber-300">Use at your own risk.</span>
            </p>
          </>
        ))
        .with("stEUR-oracle", () => (
          <>
            <p>
              The base asset in this market uses a custom oracle deployed and
              maintained by the Angle Protocol team. The oracle derives the
              price of stEUR from two sources: 1- stEUR token contract to
              calculate stEUR/agEUR value, 2- a RedStone agEUR/ETH price feed.
            </p>

            <p>
              The Silo Protocol cannot verify the security of this oracle. Use
              this market at your own risk.
            </p>

            <LearnMoreLink />
          </>
        ))
        .with("PT-eETH-oracle", () => (
          <PendleOracleLabel symbol="PT-eETH" redstoneSymbol="weETH/ETH" />
        ))
        .with("PT-ezETH-renzo-oracle", () => (
          <PendleOracleLabel symbol="PT-ezETH" redstoneSymbol="ezETH/ETH" />
        ))
        .with("PT-ezETH-chainlink-oracle", () => (
          <>
            <p>
              The value of PT-ezETH is read from an oracle developed and
              maintained by the Pendle team. To denominate the value of PT-ezETH
              in ETH, we use a Chainlink ezETH/ETH price feed.
            </p>

            <p>
              The Silo Protocol cannot verify the security of this oracle. Use
              this market at your own risk.
            </p>

            <DescriptionExternalLink href={LINKS.PT_ORACLE}>
              Lean more about the Pendle PT oracle
            </DescriptionExternalLink>

            <DescriptionExternalLink href={LINKS.CHAINLINK_EZETH_PRICE_FEED}>
              Lean about Chainlink price feeds
            </DescriptionExternalLink>
          </>
        ))
        .with("PT-rswETH-oracle", () => (
          <PendleOracleLabel symbol="PT-rswETH" redstoneSymbol="rswETH/ETH" />
        ))
        .with("PT-weETH-oracle", () => (
          <PendleOracleLabel symbol="PT-weETH" redstoneSymbol="weETH/ETH" />
        ))
        .with("PT-rsETH-oracle", () => (
          <PendleOracleLabel symbol="PT-rsETH" redstoneSymbol="rsETH/ETH" />
        ))
        .with("PT-pufETH-oracle", () => (
          <PendleOracleLabel symbol="PT-pufETH" redstoneSymbol="pufETH/ETH" />
        ))
        .with("PT-LBTC-oracle", () => (
          <PendleOracleLabel symbol="PT-LBTC" redstoneSymbol="LBTC/BTC" quoteToken="BTC" />
        ))
        .with("woETH-oracle", () => (
          <>
            <p>
              The custom Oracle computes the woETH value by converting woETH to
              oETH using the Chainlink{" "}
              <DescriptionExternalLink href={LINKS.WO_ETH_EXCHANGE_RATE}>
                woETH-oETH Exchange Rate
              </DescriptionExternalLink>{" "}
              and hardcoding 1oETH=1ETH.
            </p>

            <p>
              The Silo Protocol cannot verify the security of this oracle. Use
              this market at your own risk. Hardcoding oETH to ETH might prevent
              liquidations if oETH loses its peg to ETH. Silo Protocol cannot
              verify the precision or security of the Chainlink Exchange Rate
              contract. Use at your own risk.
            </p>
          </>
        ))
        .with("PT-USDe-oracle", () => (
          <PendleOracleLabel symbol="PT-USDe" redstoneSymbol="USDe/ETH" />
        ))
        .with("api3", () => (
          <>
            <FeedDescription
              oracleKey={oracleKey}
              oracleAssets={oracleAssets}
            />

            <p>
              While API3 is considered a trusted price provider, Silo Protocol
              cannot verify the security of their data feeds or control their
              behavior.{" "}
              <span className="text-amber-300">Use at your own risk.</span>
            </p>

            <LearnMoreLink />
          </>
        ))
        .with("wUSD+-oracle", () => (
          <>
            <p>
              wUSD+ is the 4626-vault token that represents a claim on the
              rebasing USD+. To calculate wUSD+ value, the custom oracle
              multiplies the USD+ value, fetched via a 
              <DescriptionExternalLink href={LINKS.WUSD_PLUS_CHAINLINK}>
                USD+/USD Chainlink oracle
              </DescriptionExternalLink>{" "}
              by the vault’s shares-to-assets ratio, which is set by the
              vault’s admin manually (Overnight team). Silo Protocol cannot
              verify the precision or security of data feeds nor control the
              wUSD+ vault.{" "}
              <span className="text-amber-300">Use at your own risk.</span>
            </p>

            <LearnMoreLink />
          </>
        ))
        .with("uniBTC-oracle", () => (
          <>
            <p>
              uniBTC is a native BTC wrapper. To calculate uniBTC’s market value, 
              we hardcode 1uniBTC:1BTC using a wBTC/USD Chainlink price feed. 
              The Silo Protocol cannot verify the precision or security of data feeds.
              {" "}<span className="text-amber-300">Use at your own risk.</span>
            </p>

            <LearnMoreLink />
          </>
        ))
        .with("pzETH-oracle", () => (
          <SymbioticOracleLabel symbol="pzETH" />
        ))
        .with("amphrETH-oracle", () => (
          <SymbioticOracleLabel symbol="amphrETH" />
        ))
        .with("rstETH-oracle", () => (
          <SymbioticOracleLabel symbol="rstETH" />
        ))
        .with("re7LRT-oracle", () => (
          <SymbioticOracleLabel symbol="re7LRT" />
        ))
        .with("dlcBTC-oracle", () => (
          <>
            <p>
              dlcBTC is a native BTC wrapper. To calculate dlcBTC’s market value, 
              we hardcode 1dlcBTC:1BTC using a BTC/USD Chainlink price feed. Check 
              <DescriptionExternalLink href={LINKS.DLCBTC_CHAINLINK}>
               dlcBTC’s Chainlink Proof of Reserve feed
              </DescriptionExternalLink> to verify the asset’s backing and 
              issued supply. The Silo Protocol cannot verify the precision or security of data feeds.
              {" "}<span className="text-amber-300">Use at your own risk.</span>
            </p>

            <LearnMoreLink />
          </>
        ))
        .with("sUSDz-oracle", () => (
          <>
            <p>
              The value of sUSDz is pegged to the price of 1 USDz utilizing a chainlink price feed. 
              Consequently, collateral’s Loan-to-Value (LTV) will be slightly undervalued.
              The Silo Protocol cannot verify the security of an oracle. 
              {" "}<span className="text-amber-300">Use at your own risk.</span>
            </p>

            <LearnMoreLink />
          </>
        ))
        .with("wSuperOETHb-oracle", () => (
          <>
            <div>
              <p>
                The oracle calculates the value of wsuperOETHb in superOETHb using its 
                <DescriptionExternalLink
                  className="text-left"
                  href='https://basescan.org/address/0x7FcD174E80f264448ebeE8c88a7C4476AAF58Ea6'
                >
                  ERC4262-convertToAsset function
                </DescriptionExternalLink>
                  
                and then converts it to ETH using 1superOETHb=1ETH ratio. 
              </p>
    
              <p>
                <DescriptionExternalLink
                  className="text-left"
                  href='https://basescan.org/address/0x3ed1F0487c0C102C8c371FB3e5Ad3e224DFD55C5'
                >
                  View the Oracle contract.
                </DescriptionExternalLink>
              </p>
              <p className="mt-1">
                The Silo Protocol doesn’t have any control over wsuperOETHb’s vault contract. 
              {" "}<span className="text-amber-300">Use at your own risk.</span>
              </p>
            </div>
          </>
        ))
        .with("pumpBTC-oracle", () => (
          <>
            <p>
              The value of pumpBTC is pegged to the price of 1 BTC utilizing a Chainlink price feed. 
              The Silo Protocol cannot verify the security of an oracle.
              {" "}<span className="text-amber-300">Use at your own risk.</span>
            </p>

            <LearnMoreLink />
          </>
        ))
        .exhaustive()}
    </div>
  );
}
